import { Injectable } from '@angular/core';
import { StoredCredential } from '../passkey.model.model';
import { from, Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { getDeviceId } from './device-id';

/* localStorage slot */
const STORE_KEY = 'axon-passkey';

/* util helpers */
const bufToB64 = (buf: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

const b64ToBuf = (b64: string): ArrayBuffer =>
  Uint8Array.from(atob(b64.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)).buffer;

@Injectable({
  providedIn: 'root'
})
export class PasskeyService {
  private readStore(): StoredCredential | null {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) as StoredCredential : null;
  }
  private writeStore(cred: StoredCredential) {
    localStorage.setItem(STORE_KEY, JSON.stringify(cred));
  }
  clearStore() { localStorage.removeItem(STORE_KEY); }

   /* -------------------------------- register -------------------------------- */
   startRegistration(userName: string): Observable<StoredCredential> {
    if (!window.PublicKeyCredential) {
      return throwError(() => new Error('WebAuthn not supported'));
    }

    /* In production you’d fetch this from your backend */
    const fakeChallenge = crypto.getRandomValues(new Uint8Array(32));

    const publicKey: PublicKeyCredentialCreationOptions = {
      challenge: fakeChallenge,
      rp: { id: window.location.hostname, name: 'My Angular Demo' },
      user: {
        id: new TextEncoder().encode(userName),
        name: userName,
        displayName: userName
      },
      pubKeyCredParams: [{ alg: -7, type: 'public-key' }], // ES256
      authenticatorSelection: { userVerification: 'preferred' },
      timeout: 60_000,
    };

    return from(navigator.credentials.create({ publicKey }) as Promise<PublicKeyCredential>)
      .pipe(
        map(cred => {
          const response = cred.response as AuthenticatorAttestationResponse;

          const stored: StoredCredential = {
            id: bufToB64(cred.rawId),
            publicKey: bufToB64(response.attestationObject), // demo only
            userName,
            deviceId: getDeviceId()  
          };

          this.writeStore(stored);
          return stored;
        })
      );
  }

  /* -------------------------------- authenticate -------------------------------- */
  startAuthentication(): Observable<boolean> {
    const stored = this.readStore();
    if (!stored) return of(false);

    const challenge = crypto.getRandomValues(new Uint8Array(32));

    const publicKey: PublicKeyCredentialRequestOptions = {
      challenge,
      allowCredentials: [{
        id: b64ToBuf(stored.id),
        type: 'public-key'
      }],
      userVerification: 'preferred',
      timeout: 60_000
    };

    return from(navigator.credentials.get({ publicKey }) as Promise<PublicKeyCredential>)
      .pipe(
        map(() => true),          // demo: always succeed
        catchError(() => of(false))
      );
  }

  hasPasskey(): boolean {
    return !!this.readStore();
  }


}
